import { useReducer, useEffect } from "react";
import { OrderedSet as ISet, Map } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';

import { contextReducerLogging } from 'config';
import { filterQuestions, randomlySelectQuestions, sortGroupQuestions } from 'utils/filterQuestions';
import { ExamTemplates } from 'actions/sharedJsActions';


// Importing directly from the interface here to avoid circular dependencies
import { Hooks } from '../../shared/interface/sharedJsInterface';

// Hooks
const { B2b } = Hooks;
const { useSelectedOrganizationQuestionBankIds } = B2b;

// Actions
const { examTemplateUpdate, examTemplateCreate, questionsOnTemplates } = ExamTemplates;

export const initialState = (examTemplate, subjects, markedQuestionIds, questions, step, completedSteps = [], questionBankIds) => ({
  id: examTemplate?.get('id') || 0,
  name: examTemplate?.get('name') || '',
  questionBankId: examTemplate?.get('question_bank_id') || 0,
  questionLimit: examTemplate?.get('question_limit') || 10,
  selectedQuestionIds: examTemplate?.get('question_ids') || new ISet(),
  questions: questions || new Map(),
  markedQuestionIds: markedQuestionIds || new ISet(),
  subjects: subjects || new Map(),
  examTemplate: examTemplate || new Map(),
  selectedDifficultyLevels: examTemplate?.get('difficulty_levels')?.toArray() || [],
  allDifficultiesCount: 0,
  hardCount: 0,
  moderateCount: 0,
  easyCount: 0,
  selectedQuestionType: examTemplate?.get('question_type') || 'all',
  traditionalCount: 0,
  nextGenCount: 0,
  allCount: 0,
  selectedStatus: '',
  allStatusesCount: 0,
  questionsNotOnTemplatesCount: 0,
  questionsOnTemplatesIds: [],
  questionsOnTemplateBankId: 0,
  filterMarked: false,
  markedCount: 0,
  keywordFilteredIds: new ISet(),
  selectedSubjects: subjects ? Array.from(subjects.keys()) : [],
  questionsBySubject: {},
  results: [],
  searchStatus: null,
  resultsValid: true,
  submitted: false,
  allSubjects: true,
  step: step,
  completedSteps: completedSteps,
  initialized: false,
  questionBankIds: questionBankIds,
  updatedAt: examTemplate?.get('updated_at') || 0,
  isNgnOn: false,
  standaloneQuestionIds: [],
  filteredTotalQuestionIds: [],
  groupedQuestionsByGroupId: {},
});

const requestFormatter = ({
  // filterMarked,
  name,
  questionBankId,
  // selectedStatus,
  selectedDifficultyLevels,
  selectedSubjects,
  selectedQuestionIds,
  selectedQuestionType,
  updatedAt,
}) => ({
  // marked: filterMarked,
  name,
  question_limit: selectedQuestionIds.size,
  question_bank_id: questionBankId,
  // question_mode: selectedStatus || 'All',
  difficulty_levels: selectedDifficultyLevels,
  subject_ids: selectedSubjects,
  question_ids: selectedQuestionIds.toArray(),
  question_type: selectedQuestionType,
  updated_at: updatedAt,
});

const reducer = (state, action) => {
  const { type, payload } = action;
  if (contextReducerLogging) {
    console.log('%c%s',
      'color: green; background: yellow;', type, payload);
  }

  const newState = (() => {
    switch (type) {
      case "SET_ID":
        return {
          ...state, id: payload.id, step: 1, completedSteps: [0]
        };
      case "CHANGE_QUESTION_LIMIT":
        return { ...state, questionLimit: payload.questionLimit };
      case "CHANGE_NAME":
        return { ...state, name: payload.name };
      case "CHANGE_QUESTIONS":
        return {
          ...state,
          questions: payload.questions,
          subjects: payload.subjects
        };
      case "CHANGE_QUESTION_BANK":
        return {
          ...state, questionBankId: payload.questionBankId, name: payload.name, selectedQuestionIds: new ISet(), isNgnOn: payload.isNgnOn
        };
      case "SELECT_QUESTION":
        return { ...state, selectedQuestionIds: state.selectedQuestionIds.add(payload.questionId) };
      case "SELECT_QUESTIONS":
        return { ...state, selectedQuestionIds: payload.questionIds };
      case "DESELECT_QUESTION":
        return { ...state, selectedQuestionIds: state.selectedQuestionIds.remove(payload.questionId) };
      case "CLEAR_QUESTIONS":
        return { ...state, selectedQuestionIds: new ISet() };
      case "CHANGE_NGN_STATUS":
        return {
          ...state,
          isNgnOn: payload.isNgnOn
        };
      case "SET_FORM_FROM_SEARCH":
        return { ...state, ...payload, allSubjects: false };
      case "SET_DIFFICULTY_LEVELS":
        return { ...state, selectedDifficultyLevels: [...payload.selectedDifficultyLevels] };
      case "SET_QUESTION_TYPE":
        return { ...state, selectedQuestionType: payload.selectedQuestionType };
      case "SET_QUESTION_STATUS":
        return { ...state, selectedStatus: payload.selectedStatus };
      case "SET_QUESTIONS_ON_TEMPLATES_IDS":
        return { ...state, questionsOnTemplatesIds: payload.questionsOnTemplatesIds, questionsOnTemplateBankId: payload.questionBankId };
      case "SET_KEYWORD_FILTERED_IDS":
        return { ...state, keywordFilteredIds: payload.keywordFilteredIds };
      case "CLEAR_ALL_SUBJECTS":
        return { ...state, allSubjects: false, selectedSubjects: [] };
      case "SET_ALL_SUBJECTS":
        return { ...state, allSubjects: true };
      case "SET_SELECTED_SUBJECTS":
        return { ...state, allSubjects: false, selectedSubjects: [...payload.selectedSubjects] };
      case "SET_FILTER_MARKED":
        return { ...state, filterMarked: payload.filterMarked };
      case "CLEAR_SELECTED_STATUS":
        return { ...state, selectedStatus: '' };
      case "REMOVE_SELECTED_DIFFICULTY":
        return { ...state, selectedDifficultyLevels: state.selectedDifficultyLevels.filter(diff => diff !== payload.difficulty) };
      case "CLEAR_ALL_FILTERS":
        return {
          ...state, selectedDifficultyLevels: [], selectedStatus: '', selectedSubjects: [], filterMarked: false, allSubjects: true, selectedQuestionType: ''
        };
      case "INVALIDATE_RESULTS":
        return { ...state, resultsValid: false };
      case "SET_CURRENT_STEP":
        return { ...state, step: payload.step };
      case "SET_COMPLETED_STEP":
        return { ...state, completedSteps: [...new Set(payload.completedSteps)] };
      case "VALIDATE_RESULTS":
        return { ...state, resultsValid: true };
      case "FINISH_LOADING":
        return { ...state, initialized: true };
      case "RESET_SUBMIT":
        return { ...state, submitted: false };
      case "SUBMIT_FORM":
        return { ...state, submitted: true };
      case "RELOAD_TEMPLATE":
        return {
          ...state,
          id: payload.examTemplate.get('id'),
          selectedQuestionType: payload.examTemplate.get('question_type'),
          selectedDifficultyLevels: payload.examTemplate.get('difficulty_levels').toArray(),
          name: payload.examTemplate.get('name'),
          questionBankId: payload.examTemplate.get('question_bank_id'),
          questionLimit: payload.examTemplate.get('question_limit'),
          selectedQuestionIds: payload.examTemplate.get('question_ids'),
          updatedAt: payload.examTemplate.get('updated_at'),
          step: payload.step,
          completedSteps: payload.completedSteps,
        };
      default:
        return state;
    }
  })();

  const questionsChanged = state.questions !== newState.questions || newState.allDifficultiesCount === 0;
  const difficultyChanged = state.selectedDifficultyLevels !== newState.selectedDifficultyLevels;
  const questionStatusChanged = state.selectedStatus !== newState.selectedStatus
    || state.filterMarked !== newState.filterMarked;
  const keywordFiltered = state.keywordFilteredIds !== newState.keywordFilteredIds;
  const selectedSubjectsChanged = state.selectedSubjects !== newState.selectedSubjects
    || state.allSubjects !== newState.allSubjects;
  const questionsOnTemplatesChanged = state.questionsOnTemplateBankId !== newState.questionsOnTemplateBankId;
  const questionTypeChanged = state.selectedQuestionType !== newState.selectedQuestionType;
  const isNgnOnChanged = state.isNgnOn !== newState.isNgnOn;

  const questionsInGroup = (keysArray, valuesArray, questionId) => {
    const index = valuesArray.indexOf(questionId);
    const groupId = keysArray[index];
    const indexes = [];
    const results = [];
    keysArray.filter((key, i) => key === groupId && indexes.push(i));
    indexes.forEach(indexItem => results.push(valuesArray[indexItem]));
    return sortGroupQuestions(results, newState.questions);
  };

  const caseGroupedById = [];
  const caseKeys = [];
  const caseValues = [];

  const compareSelectedSubjects = () => {
    if (state.selectedSubjects.length !== newState.selectedSubjects.length) return false;
    const compareSet = new Set();
    state.selectedSubjects.forEach(item => compareSet.add(item));
    newState.selectedSubjects.forEach(item => compareSet.add(item));
    if (compareSet.size === state.selectedSubjects.length && compareSet.size === newState.selectedSubjects.length) return true;
    return false;
  };

  const randomizeQuestions = () => {
    if (caseGroupedById.length) {
      caseGroupedById.forEach((group) => {
        caseKeys.push(Object.keys(group)[0]);
        caseValues.push(Object.values(group)[0]);
      });
    }
    if (!newState.initialized) return;
    if (newState.questionLimit === newState.selectedQuestionIds.size
      && newState.selectedQuestionIds.toArray().every(v => newState.results.includes(v))
      && compareSelectedSubjects()) return;
    if (newState.results.length !== 0 && newState.results.length <= newState.questionLimit) newState.selectedQuestionIds = new ISet(newState.results);
    if (newState.results.length > newState.questionLimit) {
      const subjectsArray = Object.keys(newState.questionsBySubject).filter(subjectId => newState.questionsBySubject[subjectId].length > 0).map(a => parseInt(a));
      const selectedSubjectsArrays = newState.selectedSubjects.length ? subjectsArray.filter(subject => newState.selectedSubjects.includes(subject)) : subjectsArray;
      const randomQuestions = new Set();
      if (newState.questions.size / 2 > newState.questionLimit) {
        let initialStep = Math.floor(Math.random() * selectedSubjectsArrays.length);
        while (newState.questionsBySubject && randomQuestions.size < newState.questionLimit) {
          const id = _.sample(newState.questionsBySubject[selectedSubjectsArrays[initialStep]]);
          if (caseValues.includes(id)) {
            const forecastNumber = randomQuestions.size + questionsInGroup(caseKeys, caseValues, id).length;
            const flexibleLimit = newState.questionLimit + 5;
            if (forecastNumber <= flexibleLimit) {
              questionsInGroup(caseKeys, caseValues, id).forEach(qId => randomQuestions.add(qId));
            }
          } else {
            randomQuestions.add(id);
          }
          initialStep = (initialStep + 1) % selectedSubjectsArrays.length;
        }
      } else {
        selectedSubjectsArrays.forEach((subjectId) => {
          newState.questionsBySubject[subjectId].forEach((questionId) => {
            randomQuestions.add(questionId);
          });
        });
        const randomQuestionArray = Array.from(randomQuestions);
        while (randomQuestions.size > newState.questionLimit) {
          const id = randomQuestionArray[Math.floor(Math.random() * randomQuestionArray.length)];
          if (caseValues.includes(id)) {
            const forecastNumber = randomQuestions.size - questionsInGroup(caseKeys, caseValues, id).length;
            const flexibleLimit = newState.questionLimit + 5;
            if (forecastNumber >= newState.questionLimit && forecastNumber <= flexibleLimit) {
              questionsInGroup(caseKeys, caseValues, id).forEach(qId => randomQuestions.delete(qId));
            }
          } else {
            randomQuestions.delete(id);
          }
        }
      }

      newState.selectedQuestionIds = new ISet(randomQuestions);
    }
  };

  // We have to recount the questions if certain inputs have been changed
  if (newState.questionBankId !== 0 && (questionsChanged || difficultyChanged || questionStatusChanged || selectedSubjectsChanged || keywordFiltered
    || questionsOnTemplatesChanged || questionTypeChanged || isNgnOnChanged)) {
    const recountQuestions = () => {
      // Reset Counts
      newState.allDifficultiesCount = 0;
      newState.hardCount = 0;
      newState.moderateCount = 0;
      newState.easyCount = 0;
      newState.allStatusesCount = 0;
      newState.questionsNotOnTemplatesCount = 0;
      newState.markedCount = 0;
      newState.traditionalCount = 0;
      newState.nextGenCount = 0;
      newState.allCount = 0;
      newState.questionsBySubject = {};
      newState.results = [];

      // Loop through questions a single time to calculate the counts for each input and the final question results
      newState.questions.forEach((question, questionId) => {
        if (question.get('ngn_type') && !newState.isNgnOn) return;

        newState.allCount += 1;
        if (question.get('ngn_type')) {
          newState.nextGenCount += 1;
        } else { newState.traditionalCount += 1; }

        if (question.get('ngn_type') && newState.selectedQuestionType === 'traditional') return;
        if (!question.get('ngn_type') && newState.selectedQuestionType === 'nextGen') return;

        const questionGroupId = question.get('group_id');
        if (questionGroupId) caseGroupedById.push({ [`${questionGroupId}`]: questionId });

        if (newState.keywordFilteredIds.size !== 0 && !newState.keywordFilteredIds.has(questionId)) return;
        newState.allDifficultiesCount += 1;
        newState[`${question.get('difficulty')}Count`] += 1;

        if (newState.selectedDifficultyLevels.length > 0 && !newState.selectedDifficultyLevels.includes(question.get('difficulty'))) return;
        newState.allStatusesCount += 1;

        if (!newState.questionsOnTemplatesIds.includes(questionId)) {
          newState.questionsNotOnTemplatesCount += 1;
        } else if (newState.selectedStatus.includes('Questions not on templates')) return;

        const isMarked = newState.markedQuestionIds.includes(questionId);
        if (isMarked) newState.markedCount += 1;
        if (newState.filterMarked && !isMarked) return;

        let hasSelectedSubject = false;
        question.get('subjectIds').forEach((subjectId) => {
          if (!newState.subjects.has(subjectId)) return;
          if (!newState.questionsBySubject[subjectId]) newState.questionsBySubject[subjectId] = [];
          newState.questionsBySubject[subjectId].push(parseInt(questionId));
          if (newState.selectedSubjects.includes(subjectId)) hasSelectedSubject = true;
        });

        if (!hasSelectedSubject) return;
        newState.results.push(questionId);
      });
    };

    recountQuestions();

    // After recounting the Questions we may need to force-change some of the users inputs
    if (newState.filterMarked && newState.markedCount < 1) {
      newState.filterMarked = false;
      recountQuestions();
    }

    let needsRecount = false;
    if (newState.allSubjects) {
      // Auto-select all subjects with questions if allSubjects is true
      newState.selectedSubjects = Array.from(newState.subjects.keys());
      needsRecount = true;
      newState.selectedSubjects.forEach((subjectId) => {
        if (!newState.questionsBySubject[subjectId]) {
          newState.selectedSubjects = newState.selectedSubjects.filter(sid => sid !== subjectId);
        }
      });
    } else if (!newState.selectedSubjects.length) {
      // Change allSubjects to false if all subjects have been deselected.
      newState.allSubjects = false;
    } else {
      // Deselect subjects without any questions
      newState.selectedSubjects.forEach((subjectId) => {
        if (!newState.questionsBySubject[subjectId]) {
          newState.selectedSubjects = newState.selectedSubjects.filter(sid => sid !== subjectId);
          needsRecount = true;
        }
      });
    }
    if (needsRecount) recountQuestions();
    randomizeQuestions();
  }
  if (!newState.isNgnOn && state.questionLimit !== newState.questionLimit) randomizeQuestions();

  if (newState.isNgnOn && newState.questionBankId !== 0 && (questionsChanged || difficultyChanged || questionStatusChanged || selectedSubjectsChanged || keywordFiltered
    || questionsOnTemplatesChanged || questionTypeChanged || isNgnOnChanged)) {
    const data = filterQuestions(newState);

    newState.standaloneQuestionIds = data.standaloneQuestionIds;
    newState.filteredTotalQuestionIds = data.filteredTotalQuestionIds;
    newState.groupedQuestionsByGroupId = data.groupedQuestionsByGroupId;
    newState.groupedTotalQuestionsByGroupId = data.groupedTotalQuestionsByGroupId;
  }

  if (newState.isNgnOn && state.questionLimit !== newState.questionLimit) {
    newState.selectedQuestionIds = randomlySelectQuestions(newState);
  }

  return newState;
};

export function useExamTemplateReducer(examTemplate, assignmentFlow = false) {
  const reduxDispatch = useDispatch();
  const navigate = useNavigate();
  const questions = useSelector(store => store.performanceQuestions);
  const subjects = useSelector(store => store.subjects);
  const organizationId = useSelector(store => store.session.get('selected_organization_id'));
  const markedQuestionIds = useSelector(store => store.users.getIn([store.session.get('current_user_id'), 'marked_question_ids']));
  const step = examTemplate?.get('id') ? 1 : 0;
  const completedSteps = (step === 1) ? [0] : [];
  const questionBankIds = useSelectedOrganizationQuestionBankIds();
  const [state, dispatch] = useReducer(reducer, initialState(examTemplate, subjects, markedQuestionIds, questions, step, completedSteps, questionBankIds));
  const {
    id,
    submitted,
    hardCount,
    moderateCount,
    easyCount,
    selectedDifficultyLevels
  } = state;

  useEffect(() => {
    const availableLevels = selectedDifficultyLevels?.filter(lvl => state[`${lvl}Count`]);
    if (availableLevels.length !== selectedDifficultyLevels.length) {
      dispatch({
        type: 'SET_DIFFICULTY_LEVELS',
        payload: {
          selectedDifficultyLevels: availableLevels
        }
      });
    }
  }, [hardCount, moderateCount, easyCount]);

  useEffect(() => {
    if (!submitted) return;
    if (!id) {
      const params = (requestFormatter({ ...state }));
      reduxDispatch(examTemplateCreate(organizationId, params))
        .then((res) => {
          if (!assignmentFlow) {
            navigate(`/assignments/templates`, { state: { message: `"${state.name}" was Created` } });
          } else {
            dispatch({
              type: 'SET_ID',
              payload: { id: +res.data.id }
            });
            dispatch({
              type: 'RESET_SUBMIT',
            });
            dispatch({
              type: 'SET_CURRENT_STEP',
              payload: { step: 1 }
            });
          }
          reduxDispatch(questionsOnTemplates(params.question_bank_id));
          return res;
        })
        .catch((error) => {
          alert(error);
        });
    } else {
      const params = (requestFormatter({ ...state }));
      reduxDispatch(examTemplateUpdate(id, params))
        .then((res) => {
          if (!assignmentFlow) {
            navigate('/assignments/templates', { state: { message: `"${state.name}" was Successfully Updated` } });
          } else {
            dispatch({
              type: 'SET_CURRENT_STEP',
              payload: { step: 1 }
            });
            dispatch({
              type: 'SET_COMPLETED_STEP',
              payload: { completedSteps: [...state.completedSteps, 0] }
            });
            dispatch({
              type: 'RESET_SUBMIT',
            });
          }
          reduxDispatch(questionsOnTemplates(params.question_bank_id));
          return res;
        })
        .catch((error) => {
          alert(error);
        });
    }
  }, [submitted]);

  return {
    state,
    dispatch
  };
}
